import { useEffect, useState } from "react";
import { ADMIN_LOGIN_EMAIL, type ApprovalDTO, type MemoryDTO, type NoteDTO, type NoteItemDTO, type NoteVersionDTO, type ScheduleDTO, type UserDTO } from "../../shared/types";
import { useUser } from "../App";
import { NoteCard } from "../components/NoteCard";
import { ConnectedChannels } from "../components/ConnectedChannels";
import { EmptyRow, List, listRowCls, ListRow } from "../components/List";
import { Tabs, useTabParam } from "../components/Tabs";
import { MySkills } from "../components/MySkills";
import { ScheduleList } from "../components/Schedules";
import { Tokens } from "../components/Tokens";
import { confirmDialog } from "../components/Dialog";
import { Button, ErrorText, inputCls, PageHeader, Panel, SectionHead, timeAgo } from "../components/ui";
import { del, get, patch, post, put } from "../lib/api";
import { changedBy } from "../lib/memory";

/** How an item got into the note, in the words the person reads. */
const ORIGIN: Record<NoteItemDTO["origin"], string> = {
  told: "because you said so",
  own: "added by the agent",
  suggested: "a suggestion you accepted",
};

/** Both parts against the one budget. It turns amber near the end and red once there is no room left. */
function SizeMeter({ note }: { note: NoteDTO }) {
  const pct = Math.min(100, Math.round((note.chars / note.maxChars) * 100));
  const tone = pct >= 100 ? "bg-danger" : pct >= 85 ? "bg-warning" : "bg-success";
  return (
    <div className="flex items-center gap-3">
      <div className="h-1.5 w-36 overflow-hidden rounded-full bg-fill-strong">
        <div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted">
        {note.chars} of {note.maxChars} characters, both parts together
      </span>
    </div>
  );
}

/** The part only the person writes. Every save is a version, and an older one can be brought back. */
function InYourWords({ note, onChanged }: { note: NoteDTO; onChanged: () => Promise<void> }) {
  const [text, setText] = useState(note.text);
  const [versions, setVersions] = useState<NoteVersionDTO[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(note.text), [note.currentVersionId, note.text]);
  const dirty = text !== note.text;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
      if (versions) setVersions(await get<NoteVersionDTO[]>(`/api/notes/${note.userId}/versions`));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Panel
        title="In your words"
        hint="What every agent should know about working with you. Only you write this part; agents never change it."
        action={
          <Button
            variant="ghost"
            onClick={() => (versions ? setVersions(null) : get<NoteVersionDTO[]>(`/api/notes/${note.userId}/versions`).then(setVersions, (e) => setError(e.message)))}
          >
            {versions ? "Hide history" : `History${note.versions ? ` · ${note.versions}` : ""}`}
          </Button>
        }
      >
        <textarea
          className={`${inputCls} min-h-44`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Keep answers short. Always use metric units. Never schedule anything before 10:00 Athens time."
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <SizeMeter note={{ ...note, chars: note.chars - note.text.length + text.length }} />
          <span className="ml-auto flex gap-2">
            {dirty && (
              <Button disabled={busy} onClick={() => setText(note.text)}>
                Undo
              </Button>
            )}
            <Button variant="primary" disabled={busy || !dirty} onClick={() => run(() => put(`/api/notes/${note.userId}`, { text }))}>
              Save
            </Button>
          </span>
        </div>
        <ErrorText error={error} />
      </Panel>
      {versions && (
        <List title="History" empty="Nothing saved yet.">
          {versions.map((v, i) => (
            <ListRow
              key={v.id}
              title={
                <span className="text-xs text-muted">
                  {i === 0 ? "Current" : `Saved ${timeAgo(v.createdAt)}`}
                  {v.savedByName ? ` · by ${v.savedByName}` : ""}
                  {v.restoredFromId ? " · restored" : ""}
                </span>
              }
              trailing={
                i > 0 && (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => post(`/api/notes/${note.userId}/versions/${v.id}/restore`))}>
                    Restore
                  </Button>
                )
              }
            >
              <p className="mt-1.5 text-sm whitespace-pre-wrap text-muted">{v.text || <span className="text-faint">(empty)</span>}</p>
            </ListRow>
          ))}
        </List>
      )}
    </>
  );
}

/** One line of what an agent learned, with where it came from, and the person's own edit and delete. */
function LearnedItem({ item, onChanged }: { item: NoteItemDTO; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setEditing(false);
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className={listRowCls}>
      {editing ? (
        <textarea className={inputCls} rows={2} autoFocus maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
      ) : (
        <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-faint">
        <span>
          {ORIGIN[item.origin]}
          {item.agentName ? ` · ${item.agentName}` : ""} · {timeAgo(item.createdAt)}
        </span>
        <span className="ml-auto flex gap-1">
          {editing ? (
            <>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => (setEditing(false), setText(item.text))}>
                Cancel
              </Button>
              <Button variant="ghost" size="sm" disabled={busy || !text.trim()} onClick={() => run(() => patch(`/api/notes/${item.userId}/items/${item.id}`, { text }))}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="danger" size="sm" disabled={busy} onClick={async () => (await confirmDialog({ title: "Delete this from your note?", body: "Agents stop reading it from their next turn.", confirm: "Delete", danger: true })) && run(() => del(`/api/notes/${item.userId}/items/${item.id}`))}>
                Delete
              </Button>
            </>
          )}
        </span>
      </div>
      <ErrorText error={error} />
    </div>
  );
}

/** "How to work with me": the two parts of the note, and the suggestions still waiting for an answer. */
function HowToWorkWithMe({ note, onChanged }: { note: NoteDTO; onChanged: () => Promise<void> }) {
  const [waiting, setWaiting] = useState<ApprovalDTO[]>([]);
  const loadWaiting = () =>
    get<ApprovalDTO[]>("/api/approvals?view=mine&status=pending").then((list) => setWaiting(list.filter((a) => a.kind === "note")), () => {});
  useEffect(() => {
    void loadWaiting();
  }, [note.items.length]);

  return (
    <div className="space-y-6">
      <InYourWords note={note} onChanged={onChanged} />
      {waiting.length > 0 && (
        <section className="min-w-0">
          <SectionHead title={`Suggestions waiting · ${waiting.length}`} hint="An agent was not sure, so it asked first. Add it, change the words first, or say no." />
          <div className="space-y-3">
            {waiting.map((a) => (
              <NoteCard key={a.id} approval={a} canDecide />
            ))}
          </div>
        </section>
      )}
      <List
        title={`Learned by agents · ${note.items.length}`}
        hint="What agents picked up working with you. Every one of them is yours to reword or delete. They share the size above with what you wrote."
        empty='Nothing yet. Tell an agent "always…" or "never…" and it will add it here, and say that it did.'
      >
        {note.items.map((i) => (
          <LearnedItem key={i.id} item={i} onChanged={onChanged} />
        ))}
      </List>
    </div>
  );
}

/** What agents remember about this person, which is everything that is not in the note: searched, not always loaded. */
function WhatAgentsRemember({ userId }: { userId: string }) {
  const [memories, setMemories] = useState<MemoryDTO[] | null>(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const load = (query = q) =>
    get<MemoryDTO[]>(`/api/notes/${userId}/memories${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`).then(setMemories, (e) => setError(e.message));
  useEffect(() => {
    void load("");
  }, [userId]);

  return (
    <List
      title={memories ? `Memories about you · ${memories.length}` : "Memories about you"}
      hint="Agents search these when they need them; none of them is loaded into every conversation. Your note is the part that always is."
      footer={error && <ErrorText error={error} />}
      filters={
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void load();
          }}
        >
          <input className={`${inputCls} flex-1`} placeholder="Search what agents remember" value={q} onChange={(e) => setQ(e.target.value)} />
          <Button type="submit">Search</Button>
        </form>
      }
    >
      {memories?.map((m) => (
        <div key={m.id} className={listRowCls}>
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-medium">{m.title}</span>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => (editing === m.id ? setEditing(null) : (setEditing(m.id), setDraft(m.content)))}
            >
              {editing === m.id ? "Cancel" : "Edit"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="shrink-0"
              onClick={async () =>
                (await confirmDialog({ title: `Forget "${m.title}"?`, body: "Agents can no longer find it.", confirm: "Forget", danger: true })) &&
                del(`/api/notes/${userId}/memories/${m.id}`).then(() => load(), (e) => setError(e.message))
              }
            >
              Forget
            </Button>
          </div>
          {editing === m.id ? (
            <div className="mt-2 space-y-2">
              <textarea className={inputCls} rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} />
              <Button
                variant="primary"
                disabled={!draft.trim()}
                onClick={() =>
                  patch(`/api/notes/${userId}/memories/${m.id}`, { content: draft }).then(
                    () => (setEditing(null), load()),
                    (e) => setError(e.message),
                  )
                }
              >
                Save
              </Button>
            </div>
          ) : (
            <p className="mt-1.5 text-sm whitespace-pre-wrap text-muted">{m.content}</p>
          )}
          <div className="mt-2 text-xs text-faint">
            {m.level === "user_agent" ? "One agent's memory of you" : "Every agent's memory of you"}{changedBy(m) ? ` · changed by ${changedBy(m)}` : ""} · updated {timeAgo(m.updatedAt)}
          </div>
        </div>
      ))}
      {memories?.length === 0 && <EmptyRow>Nothing remembered about you yet.</EmptyRow>}
    </List>
  );
}

/** The details admins and the HR sync keep. Read-only here on purpose: agents never write them either. */
function MyDetails({ user }: { user: UserDTO }) {
  const rows: [string, string | null][] = [
    ["Name", user.name],
    ["Email", user.email],
    ["Role in AgentOS", user.role === "admin" ? "Admin" : "Member"],
    ["Job title", user.jobTitle],
    ["Department", user.department],
    ["Location", user.location],
    ["Manager", user.manager],
    ["GitHub", user.githubUsername ? `@${user.githubUsername}` : null],
  ];
  return (
    <Panel title="My details" hint="Agents read these and never change them. To correct something here, ask an admin.">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-xs font-medium text-muted sm:pt-0.5">{k}</dt>
            <dd className="min-w-0 break-words text-muted">{v || <span className="text-faint">—</span>}</dd>
          </div>
        ))}
      </dl>
      {user.profileSource === "bamboohr" && <p className="mt-4 text-xs text-faint">Job title, department, location and manager come from BambooHR{user.profileSyncedAt ? `, last synced ${timeAgo(user.profileSyncedAt)}` : ""}.</p>}
    </Panel>
  );
}

/**
 * A person's schedules (D6): every one is theirs — made by them or by an agent working for them — runs as them and
 * answers in their own chat with that agent. Made in a chat ("remind me in 3 hours") or on an agent's page.
 */
function MySchedules() {
  const [schedules, setSchedules] = useState<ScheduleDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => get<ScheduleDTO[]>("/api/schedules?mine=1").then(setSchedules, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  if (!schedules) return error ? <ErrorText error={error} /> : <p className="text-sm text-muted">Loading…</p>;
  return (
    <ScheduleList
      title={`My schedules · ${schedules.length}`}
      hint="Each runs as you and answers in your own chat with that agent, and wherever else you send it. Ask Pandito for one — “remind me in 3 hours”, “every Monday send me the pipeline” — and it lands here."
      empty="Nothing scheduled. Ask Pandito for something later or every week, and it shows up here."
      schedules={schedules}
      ownsAgent={() => false}
      showAgent
      onChanged={load}
    />
  );
}

const TABS = [
  ["note", "How to work with me"],
  ["schedules", "My schedules"],
  ["skills", "My skills"],
  ["memories", "What agents remember"],
  ["channels", "Connected channels"],
  ["tokens", "Tokens"],
  ["details", "My details"],
] as const;

/** Everyone's own page: the note every agent working for them reads, what they remember, and the details they cannot change. */
export function AboutMePage() {
  const user = useUser();
  const [tab, setTab] = useTabParam(TABS.map(([key]) => key));
  const [note, setNote] = useState<NoteDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setNote(await get<NoteDTO>(`/api/notes/${user.id}`));
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, [user.id]);

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-8 md:py-10">
        <PageHeader title="About me" subtitle="What every agent knows about working with you, and what they remember." />
        <Tabs label="Section" className="mb-6" value={tab} onChange={setTab} tabs={TABS.map(([key, label]) => ({ key, label }))} />
        <ErrorText error={error} />
        {tab === "note" && (note ? <HowToWorkWithMe note={note} onChanged={load} /> : <p className="text-sm text-muted">Loading…</p>)}
        {tab === "schedules" && <MySchedules />}
        {tab === "skills" && <MySkills userId={user.id} />}
        {tab === "memories" && <WhatAgentsRemember userId={user.id} />}
        {tab === "channels" && <ConnectedChannels userId={user.id} title="Connected channels" hint="Where agents recognise you. Whatever door you use, it is the same conversation." />}
        {tab === "tokens" && (
          <Panel title="Tokens" hint="A token signs in as you, with your role, from a script or a tool. It is shown once when made; revoke it when it is done.">
            {user.email === ADMIN_LOGIN_EMAIL ? (
              <p className="text-sm text-muted">
                The shared Admin login holds no token of its own. Give a script or a tool a service account (Admin → Users → New user), or sign in as
                yourself and make your own here.
              </p>
            ) : (
              <Tokens userId={user.id} self />
            )}
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-line pt-4">
              <div className="text-xs text-muted">Signed in somewhere you no longer are? Every session of yours ends, this one included.</div>
              <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                  if (!(await confirmDialog({ title: "Sign out everywhere?", body: "Every browser and token session of yours ends, this one included.", confirm: "Sign out everywhere", danger: true }))) return;
                  await post("/api/me/sessions/revoke", {}).catch(() => {});
                  location.href = "/login";
                }}
              >
                Sign out everywhere
              </Button>
            </div>
          </Panel>
        )}
        {tab === "details" && <MyDetails user={user} />}
      </div>
    </div>
  );
}
